import { useState } from "react";
import { useQuery, getWalletByPropFirm, useAction } from "wasp/client/operations";
import { Pencil, Plus } from "lucide-react";
import { Button } from "../../../client/components/ui/button";
import { Card } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { WalletAddModal } from "../../../prop-firm-manager/WalletAddModal";
import { WalletInitialBalanceBanner } from "../../../prop-firm-manager/WalletInitialBalanceBanner";
import { WalletInitialBalanceModal } from "../../../prop-firm-manager/WalletInitialBalanceModal";
import { usePfmData } from "../../../prop-firm-manager/PfmDataContext";
import useTranslation from "../../../client/hooks/useTranslation";

export default function PropFirmWalletBalanceWidget() {
  const t = useTranslation();
  const w = t.propFirmManager.wallet;
  const { tptWalletContext, isDark } = usePfmData();
  const tptTemplateId = (tptWalletContext as any)?.propFirmTemplateId ?? null;
  const tptAccounts = (tptWalletContext as any)?.accounts ?? [];

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditInitial, setShowEditInitial] = useState(false);

  const { data: wallet, refetch } = useQuery(getWalletByPropFirm, tptTemplateId ? { propFirmTemplateId: tptTemplateId } : undefined, { enabled: !!tptTemplateId });

  if (!tptTemplateId) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader label={w.title} isDark={isDark} compact />
        <div className="flex-1 flex flex-col items-center justify-center gap-1 px-4 py-6 text-center">
          <span className="text-sm font-medium text-foreground">{w.emptyTitle}</span>
          <span className="text-xs text-muted-foreground max-w-xs">{w.emptyDesc}</span>
        </div>
      </Card>
    );
  }

  const onMutated = () => refetch();
  const approvedSum = (wallet as any)?.approvedTransfersSum ?? 0;
  const completedSum = (wallet as any)?.completedWithdrawalsSum ?? 0;
  const initialBalance = (wallet as any)?.initialBalance ?? 0;
  const available = (wallet as any)?.availableBalance ?? 0;
  const transferCount = (wallet as any)?.transfers?.length ?? 0;
  const withdrawalCount = (wallet as any)?.withdrawals?.length ?? 0;

  return (
    <div className="h-full flex flex-col gap-2">
      <WalletInitialBalanceBanner propFirmTemplateId={tptTemplateId} onMutated={onMutated} />
      <Card className="flex-1 flex flex-col">
        <CardSectionHeader
          label={w.title}
          isDark={isDark}
          compact
          rightContent={
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowEditInitial(true)} title={w.editInitialBalance}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" className="h-7 gap-1 text-xs" onClick={() => setShowAddModal(true)}>
                <Plus className="h-3.5 w-3.5" />
                {w.add}
              </Button>
            </div>
          }
        />
        <div className="flex-1 flex items-center gap-4 px-4 py-2 min-w-0 flex-wrap">
          <div className="flex flex-col min-w-0">
            <span className="text-2xl font-bold font-mono text-primary leading-none tabular-nums">
              ${available.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[11px] text-muted-foreground mt-0.5">{w.availableBalance}</span>
          </div>
          <div className="flex items-center gap-4 text-xs ml-auto flex-wrap">
            <div className="flex flex-col items-end">
              <span className="font-mono tabular-nums">${initialBalance.toFixed(2)}</span>
              <span className="text-[10px] text-muted-foreground">{w.initialBalance}</span>
            </div>
            <div className="flex flex-col items-end">
              <span className="font-mono tabular-nums text-pnlPositive">+${approvedSum.toFixed(2)}</span>
              <span className="text-[10px] text-muted-foreground">{w.transfersReceived} ({transferCount})</span>
            </div>
            <div className="flex flex-col items-end">
              <span className="font-mono tabular-nums text-red-400">-${completedSum.toFixed(2)}</span>
              <span className="text-[10px] text-muted-foreground">{w.withdrawn} ({withdrawalCount})</span>
            </div>
          </div>
        </div>
      </Card>
      <WalletAddModal open={showAddModal} onClose={() => setShowAddModal(false)} propFirmTemplateId={tptTemplateId} tptAccounts={tptAccounts} onMutated={onMutated} />
      <WalletInitialBalanceModal open={showEditInitial} onClose={() => setShowEditInitial(false)} propFirmTemplateId={tptTemplateId} currentInitialBalance={(wallet as any)?.initialBalance} currentInitialBalanceDate={(wallet as any)?.initialBalanceDate} onMutated={onMutated} />
    </div>
  );
}
